import React, { useState, useEffect, useCallback } from "react";
import styles from "./AbsencesList.module.sass";
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Icon from "../../components/Icon";
import Filters from "../../components/Filters";

import Table from "./Table";
import Settings from "./Settings";
import ModalExportAbsences from "../../components/ModalExportAbsences";
import Actions from "../../components/Actions";

import { getAttendances, updateAttendance } from "../../services/attendances.service";
import { createStudentMemo } from "../../services/studentMemo.service";
import { useAuth } from "../../contexts/AuthContext";

// Fonction pour formater la date au format "DD/MM/YYYY"
const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
};

const AbsencesList = () => {
    const { currentUser, getPartnerId } = useAuth();
    const [search, setSearch] = useState("");
    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // États pour les filtres
    const [filters, setFilters] = useState({
        sorting: "Plus récents",
        formationId: null,
        justifications: []
    });
    
    // État pour la modal d'export
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');

    // Options d'export
    const exportOptions = [
        {
            title: "Exporter en PDF",
            action: () => {
                setExportFormat('pdf');
                setShowExportModal(true);
            }
        },
        {
            title: "Exporter en Excel",
            action: () => {
                setExportFormat('excel');
                setShowExportModal(true);
            }
        }
    ];

    const fetchAbsences = useCallback(async () => {
        try {
            setLoading(true);
            
            // Construire le filtre de recherche
            let filterParts = ['status = "Absent"'];
            
            // Filtre par partner_id
            const partnerId = getPartnerId();
            if (partnerId) {
                filterParts.push(`session_id.partner_id = "${partnerId}"`);
            }
            
            if (search.trim()) {
                filterParts.push(`(session_id.title~'${search.trim()}' || participant_id.first_name~'${search.trim()}' || participant_id.last_name~'${search.trim()}')`);
            }
            
            // Filtre par formation
            if (filters.formationId) {
                filterParts.push(`session_id.formation_id = "${filters.formationId}"`);
            }
            
            // Filtre par justification
            if (filters.justifications.length === 1) {
                if (filters.justifications.includes("Justifiée")) {
                    filterParts.push(`status_justification = "Absence justifiée"`);
                } else if (filters.justifications.includes("Non justifiée")) {
                    filterParts.push(`(status_justification = "Absence injustifiée" || status_justification = "")`);
                }
            }
            
            const filterString = filterParts.join(' && ');
            
            // Déterminer le tri
            let sortString = "-created";
            if (filters.sorting === "Plus anciens") {
                sortString = "+created";
            }
            
            const result = await getAttendances({
                filter: filterString,
                sort: sortString,
                page,
                perPage: 10,
                expand: 'participant_id,session_id,session_id.teacher_id,session_id.formation_id'
            });
            
            // Transformer les absences pour correspondre au format attendu
            const formattedAbsences = result.items.map(attendance => {
                const session = attendance.expand?.session_id;
                const teacher = session?.expand?.teacher_id;
                const student = attendance.expand?.participant_id;
                
                return {
                    id: attendance.id,
                    studentId: student?.id || '',
                    studentName: student ? `${student.first_name || ''} ${student.last_name || ''}`.trim() : 'Élève non spécifié',
                    studentEmail: student?.email || '',
                    sessionName: session?.title || 'Séance sans nom',
                    teacherName: teacher ? `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() : 'Formateur non spécifié',
                    date: session?.start_date ? formatDateTime(session.start_date) : 'Date non spécifiée',
                    startTime: session?.start_date || '',
                    endTime: session?.end_date || '',
                    status: attendance.status,
                    justification: attendance.status_justification === 'Absence justifiée',
                    originalAttendance: attendance
                };
            });
            
            setAbsences(formattedAbsences);
            setTotalPages(result.totalPages || 1);
        } catch (error) {
            console.error('Erreur lors de la récupération des absences:', error);
        } finally {
            setLoading(false);
        }
    }, [page, search, filters]);

    useEffect(() => {
        fetchAbsences();
    }, [fetchAbsences]);

    const handleSearch = (e) => {
        e?.preventDefault();
        setPage(1);
        fetchAbsences();
    };

    // Gestionnaire pour les changements de filtres
    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Fonction pour justifier une absence
    const handleJustify = async (item) => {
        try {
            await updateAttendance(item.id, { status_justification: 'Absence justifiée' });
            
            // Créer un mémo pour tracer la justification
            const today = new Date().toLocaleDateString('fr-FR');
            const memoData = {
                student_id: item.studentId,
                topic: `Absence justifiée - ${item.sessionName}`,
                content: JSON.stringify({
                    blocks: [{
                        key: 'memo',
                        text: `Absence du ${item.date} (${item.sessionName}) marquée comme justifiée le ${today}.`,
                        type: 'unstyled',
                        depth: 0,
                        inlineStyleRanges: [],
                        entityRanges: [],
                        data: {}
                    }],
                    entityMap: {}
                }),
                created_by: currentUser?.id
            };
            await createStudentMemo(memoData);
            
            // Mettre à jour l'état local
            setAbsences(prevAbsences => 
                prevAbsences.map(absence => 
                    absence.id === item.id 
                        ? { ...absence, justification: true }
                        : absence
                )
            );
        } catch (error) {
            console.error('Erreur lors de la justification:', error);
        }
    };

    // Fonction pour retirer la justification d'une absence
    const handleUnjustify = async (item) => {
        try {
            await updateAttendance(item.id, { status_justification: 'Absence injustifiée' });
            // Mettre à jour l'état local
            setAbsences(prevAbsences => 
                prevAbsences.map(absence => 
                    absence.id === item.id 
                        ? { ...absence, justification: false }
                        : absence
                )
            );
        } catch (error) {
            console.error('Erreur lors du retrait de la justification:', error);
        }
    };

    return (
        <>
            <Card
            className={styles.card}
            classCardHead={styles.head}
            title="Absences"
            classTitle={cn("title-red", styles.title)}
            head={
                <>
                    <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={handleSearch}
                        placeholder="Rechercher par élève, séance ou formateur"
                        type="text"
                        name="search"
                        icon="search"
                    />
                    <Filters
                        className={styles.filters}
                        title="Filtrer"
                    >
                        <Settings onFiltersChange={handleFiltersChange} />
                    </Filters>
                    <Actions
                        className={styles.actions}
                        classActionsHead={styles.actionsHead}
                        classActionsBody={styles.actionsBody}
                        classActionsOption={styles.actionsOption}
                        items={exportOptions}
                        buttonText="Exporter"
                        buttonIcon="download"
                    />
                </>
            }
        >
            <div className={styles.wrapper}>
                {loading ? (
                    <div className={styles.loading}>Chargement des absences...</div>
                ) : absences.length > 0 ? (
                    <>
                        <Table 
                            items={absences} 
                            onJustify={handleJustify}
                            onUnjustify={handleUnjustify}
                        />
                        <div className={styles.foot}>
                            <button 
                                className={styles.arrow} 
                                onClick={() => handlePageChange(Math.max(1, page - 1))} 
                                disabled={page === 1}
                            >
                                <Icon name="arrow-left" size="20" />
                            </button>
                            <span className={styles.pagination}>
                                Page {page} sur {totalPages}
                            </span>
                            <button 
                                className={styles.arrow} 
                                onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
                                disabled={page === totalPages}
                            >
                                <Icon name="arrow-right" size="20" />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles.empty}>Aucune absence enregistrée</div>
                )}
            </div>
        </Card>

            {/* Modal d'export des absences */}
            <ModalExportAbsences
                visible={showExportModal}
                onClose={() => setShowExportModal(false)}
                partnerId={getPartnerId()}
                exportFormat={exportFormat}
            />
        </>
    );
};

export default AbsencesList;
